// Notes.js - Complete with Edit + Delete functionality

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';

const API_BASE_URL = 'https://super-backend.hira-tech.co.uk';

const api = axios.create({ baseURL: API_BASE_URL });

const getAuthToken = () => {
  const token = localStorage.getItem('access_token') ||
                localStorage.getItem('token') ||
                localStorage.getItem('accessToken');
  if (token) return token;
  const possibleKeys = ['token', 'authToken', 'access_token', 'accessToken', 'jwt'];
  for (let key of possibleKeys) {
    const storedData = localStorage.getItem(key);
    if (storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (parsed.access || parsed.token || parsed.access_token) {
          return parsed.access || parsed.token || parsed.access_token;
        }
      } catch (e) { return storedData; }
    }
  }
  return null;
};

api.interceptors.request.use(
  (config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error) => Promise.reject(error)
);

const Notes = ({ orderId, shopId, order }) => {
  const [notes, setNotes] = useState('');
  const [notesList, setNotesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState('');
  const [updating, setUpdating] = useState(false);

  const getCurrentShopId = () => {
    if (shopId) return shopId;
    if (order && order.shop_id) return order.shop_id;
    if (order && order.shop && order.shop.shop_id) return order.shop.shop_id;
    if (order && order.shop_details && order.shop_details.shop_id) return order.shop_details.shop_id;

    const urlParams = new URLSearchParams(window.location.search);
    const urlShopId = urlParams.get('shop_id');
    if (urlShopId) return urlShopId;

    return localStorage.getItem('selectedShopId');
  };

  const fetchNotes = async () => {
    if (!orderId) return;
    setLoading(true);
    try {
      const response = await api.get(`/order/api/order-notes/?order_id=${orderId}`);
      let data = Array.isArray(response.data) ? response.data : [];

      // ✅ FILTER: Remove the main "Place Order" note if the backend accidentally duplicated it
      if (order && order.note) {
        data = data.filter(n => n.note !== order.note);
      }

      setNotesList(data);
    } catch (error) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotes();
  }, [orderId]);

  const addNote = async () => {
    if (!notes.trim()) return;

    const currentShopId = getCurrentShopId();
    if (!currentShopId) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Shop ID not found. Please refresh the page.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    setSaving(true);
    try {
      await api.post('/order/api/order-notes/create/', {
        order_id: orderId,
        note: notes,
        shop_id: currentShopId,
        is_required: false
      });

      setNotes('');
      await fetchNotes();

      Swal.fire({
        icon: 'success',
        title: 'Note Added',
        text: 'Your note has been saved successfully.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error adding note:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to add note. Please try again.',
        confirmButtonColor: '#004aad'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      addNote();
    }
  };

  const startEdit = (note) => {
    setEditingId(note.id);
    setEditingText(note.note || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingText('');
  };

  const saveEdit = async (id) => {
    if (!editingText.trim()) {
      Swal.fire({
        icon: 'warning',
        title: 'Empty Note',
        text: 'Note cannot be empty.',
        confirmButtonColor: '#004aad'
      });
      return;
    }

    const currentShopId = getCurrentShopId();
    setUpdating(true);

    try {
      // ✅ Changed to PUT and added all required fields to satisfy backend
      await api.put(`/order/api/order-notes/${id}/`, {
        order_id: orderId,
        note: editingText.trim(),
        shop_id: currentShopId,
        is_required: false
      });

      setEditingId(null);
      setEditingText('');
      await fetchNotes();

      Swal.fire({
        icon: 'success',
        title: 'Updated',
        text: 'Note updated successfully.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Error updating note:', error);
      // ✅ Show the exact backend error so you know what went wrong
      const backendMsg = error.response?.data?.detail || error.response?.data?.note?.[0] || error.response?.data?.error || 'Failed to update note.';
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: backendMsg,
        confirmButtonColor: '#004aad'
      });
    } finally {
      setUpdating(false);
    }
  };

  const deleteNote = async (id) => {
    const result = await Swal.fire({
      title: 'Delete Note',
      text: 'Are you sure you want to delete this note?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#004aad',
      confirmButtonText: 'Yes, Delete',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/order/api/order-notes/${id}/`);
        await fetchNotes();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Note has been deleted.',
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error deleting note:', error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete note.',
          confirmButtonColor: '#004aad'
        });
      }
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="notes-content">
      <div className="notes-container">
        <h3 className="notes-title">Production Notes</h3>

        <div className="notes-display">
          {loading ? (
            <p className="no-notes-message">Loading notes...</p>
          ) : notesList.length === 0 ? (
            <p className="no-notes-message">No notes available. Add your first note below.</p>
          ) : (
            <div className="notes-list">
              {notesList.map((note) => (
                <div key={note.id} className="note-item">
                  {editingId === note.id ? (
                    <div>
                      <div className="note-header">
                        <span className="note-date">{formatDate(note.created_at)}</span>
                      </div>
                      <textarea
                        className="notes-textarea-new"
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        disabled={updating}
                        style={{ width: '100%', marginBottom: '8px' }}
                      />
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button
                          onClick={cancelEdit}
                          disabled={updating}
                          style={{ padding: '6px 14px', border: '1px solid #ccc', background: '#fff', borderRadius: '4px', cursor: updating ? 'not-allowed' : 'pointer', color: '#555', fontWeight: '600' }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => saveEdit(note.id)}
                          disabled={updating}
                          style={{ padding: '6px 14px', border: 'none', background: '#004aad', color: '#fff', borderRadius: '4px', cursor: updating ? 'not-allowed' : 'pointer', fontWeight: '600', opacity: updating ? 0.7 : 1 }}
                        >
                          {updating ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="note-header">
                        <span className="note-date">{formatDate(note.created_at)}</span>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            className="edit-note-btn"
                            onClick={() => startEdit(note)}
                            title="Edit Note"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '2px 6px' }}
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-note-btn"
                            onClick={() => deleteNote(note.id)}
                            title="Delete Note"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                      <div className="note-text">{note.note}</div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="notes-input-section">
          <textarea
            className="notes-textarea-new"
            placeholder="Add a new note..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            onKeyPress={handleKeyPress}
            rows={4}
            disabled={saving}
          />
          <button
            className="add-note-btn"
            onClick={addNote}
            disabled={saving || !notes.trim()}
          >
            {saving ? 'Saving...' : 'Add Note'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Notes;